import { Component, EventEmitter, inject, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../../../core/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  FormErrorDisplayComponent
} from '../../../../shared/components/form-validation-errors/form-error-display/form-error-display';
import {
  FormValidationErrorService
} from '../../../../shared/components/form-validation-errors/form-validation.service';
import { Message } from 'primeng/message';
import { InputText } from 'primeng/inputtext';
import { SsoButtonsComponent } from '../../sso-buttons/sso-buttons';
import { AppButton } from '../../../client/components/atom/app-button/app-button';

@Component({
  selector: 'app-login-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormErrorDisplayComponent,
    Message,
    InputText,
    TranslatePipe,
    SsoButtonsComponent,
    AppButton
  ],
  templateUrl: './login-form.html',
  styleUrl: './login-form.scss'
})
export class LoginForm implements OnInit, OnDestroy {
  @Output() success = new EventEmitter<void>();
  @Output() switchToRegister = new EventEmitter<void>();

  loginForm: FormGroup;
  loading = signal(false);
  incorrectCredentials = false;
  isForgotPasswordMode = false;
  forgotPasswordMessage: { type: 'success' | 'error', text: string } | null = null;
  protected formValidation = inject(FormValidationErrorService);

  readonly illustrationUrl = 'assets/auth/login.webp';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private toastService: ToastService,
    private translate: TranslateService
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.formValidation.registerForm(this.loginForm);
  }

  ngOnDestroy(): void {
    this.formValidation.unregisterForm();
  }

  onSubmit(): void {
    this.incorrectCredentials = false;
    if (!this.loginForm.valid) {
      this.loginForm.markAllAsTouched();
      this.toastService.warn(this.translate.instant('auth.fill_required_fields'));
      return;
    }
    this.loading.set(true);
    this.loginForm.disable();
    const { email, password } = this.loginForm.value;
    this.authService.login({ email, password }).subscribe({
      next: () => {
        this.success.emit();
      },
      error: (error) => {
        if (error.status === 401) this.incorrectCredentials = true;
        this.loading.set(false);
        this.loginForm.enable();
      }
    });
  }

  onForgotPassword(): void {
    const emailControl = this.loginForm.get('email');
    if (!emailControl || emailControl.invalid || !emailControl.value) {
      this.loginForm.markAllAsTouched();
      this.toastService.warn(this.translate.instant('auth.fill_required_fields'));
      return;
    }

    this.loading.set(true);
    this.authService.forgotPassword(emailControl.value).subscribe({
      next: () => {
        this.forgotPasswordMessage = {
          type: 'success',
          text: this.translate.instant('auth.login.reset_success')
        };
        this.loading.set(false);
        // Auto reset after 3 seconds
        setTimeout(() => {
          this.exitForgotPasswordMode();
        }, 3000);
      },
      error: (error) => {
        let errorMessage = this.translate.instant('app.errors.server');
        if (error?.status === 429) {
          errorMessage = this.translate.instant('auth.login.reset_throttle');
        }
        this.forgotPasswordMessage = {
          type: 'error',
          text: errorMessage
        };
        this.loading.set(false);
      }
    });
  }

  enterForgotPasswordMode(): void {
    this.isForgotPasswordMode = true;
    this.incorrectCredentials = false;
    this.forgotPasswordMessage = null;
  }

  exitForgotPasswordMode(): void {
    this.isForgotPasswordMode = false;
    this.forgotPasswordMessage = null;
  }
}
